import { TurnConfig } from '@app/shared/services/agents.service';

// --- API Models (Backend Responses) ---
export interface AgentTurnMessage {
  role: 'user' | 'assistant';
  content: string;
}

export type InterviewDepth = 'first_time' | 'regular' | 'power';
export type AgentTurnAction = 'review_and_search' | 'refine_then_review' | 'apply_staged';

export interface AgentGate {
  kind: string;
  summary?: string;
  title?: string;
  question?: string;
  header?: string;
  options?: { label: string; description: string }[];
  multi_select?: boolean;
  allow_other?: boolean;
  phase?: string;
  reranked_count?: number;
  current?: number;
  projected?: number;
  criteria?: ViewResponseCriteria[];
  [key: string]: unknown;
}

export interface AgentGatheringProgress {
  fraction: number;
  percent: number;
  stage: string;
  complete: boolean;
}

export interface AgentSearchAnchor {
  candidate_id: string;
  label: string;
  query_text: string;
}

export interface ViewResponseCriteria {
  name: string;
  weight?: number;
  must_have: boolean;
  suggested?: string;
}

export interface AgentStagedChanges {
  summary: string;
  pending_count: number;
  non_criteria_count: number;
  criteria_preview: ViewResponseCriteria[] | null;
}

export interface ViewResponse {
  messages: AgentTurnMessage[];
  current_phase: string;
  criteria: ViewResponseCriteria[];
  reranked_results: unknown[];
  search_results: unknown[];
  excluded: unknown[];
  suggestions: string[];
  gathering_progress: AgentGatheringProgress | null;
  search_anchor: AgentSearchAnchor | null;
  gate: AgentGate | null;
  staged?: AgentStagedChanges | null;
  title?: string | null;
}

export interface AgentStatePatch {
  requested_action?: 'discard_staged';
  staged_criteria?: ViewResponseCriteria[];
}

export interface TurnProgressStatus {
  message: string;
  stage?: string | null;
  seq?: number;
  holds?: string[];
}

export interface ProgressResponse {
  run_id: string;
  status: TurnProgressStatus | null;
  log?: TurnProgressStatus[];
}

// --- UI Interaction Models ---
export interface AiChatOption {
  id: string | number;
  label: string;
  selected?: boolean;
  disabled?: boolean;
  description?: string;
}

export interface AiChatSelectValue {
  selectedOptions: AiChatOption[];
  customValue?: string;
}

export interface AiChatContinueTurn {
  prompt?: string;
  resumeConfig?: TurnConfig;
  selection?: AiChatSelectValue;
  showSelectionSummary?: boolean;
  useMessageTurn?: boolean;
  action?: AgentTurnAction;
}

export interface SummaryOverview {
  title: string;
  summary: string;
  mustHave: string[];
  niceToHave: string[];
}

export interface AgentSummariesRequest {
  candidate_ids: string[];
}

export interface AgentSummariesResponse {
  summaries: Record<string, string | null>;
}

export interface AiSummaryResponse {
  candidateId: string;
  summary: string;
}

export interface AgentCandidateOverview {
  criteriaMet: string[];
  criteriaNotMet: string[];
  criteriaReasoning: Record<string, string>;
}

export const gateRendersOptions = (gate: AgentGate | null | undefined): boolean =>
  gate?.kind === 'clarify_options' && (gate.options?.length ?? 0) > 0;

export const gateQuestionBelongsInThread = (gate: AgentGate | null | undefined): boolean =>
  !!gate?.question && !gateRendersOptions(gate) && gate.kind !== 'open_question';
